/**
 * LiveLeaderboard — «живой» рейтинг, обновляющийся по событию scoreboard во время
 * игры (И7, Часть 2). Поверх ScoreList добавляет:
 *
 *   - анимацию перестановки строк (FLIP на CSS-трансформах, без JS-либ): при смене
 *     порядка элементы плавно «переезжают» на новые места, а не скачут;
 *   - медали топ-3 (🥇🥈🥉) и подсветку текущего игрока;
 *   - опциональный лимит top-N (для экрана игрока).
 *
 * Фаза 15.1 (решение владельца): в фазе вопроса у ведущего live-ростер объединён
 * с рейтингом — ОДИН список. Дополнительно к entries принимает roster-слоты:
 *   - статус `dropped` → приглушение строки (образец .roster-item--dropped) +
 *     бейдж «отвалился» (общий DroppedBadge) + tooltip времени отвала/возврата;
 *   - кнопка «удалить» в строке (контракты testid roster-kick-<playerId> /
 *     roster-kick-confirm / roster-kick-cancel) + общий KickConfirmModal;
 *   - fallback: пока scoreboard пуст (первый вопрос ещё не закрыт), строки
 *     рендерятся из roster — ник + статус + кик, без места/очков, чтобы кик и
 *     dropped были доступны уже в первом активном вопросе.
 *
 * Сопоставление roster↔scoreboard — ПО НИКУ: ScoreboardEntry не несёт playerId
 * (сервер, wire.go), ники в обоих событиях канонические; при host-rename оба
 * события приходят обновлёнными. Кикнутые исключаются сервером из roster и из
 * будущих scoreboard (rankedLocked) — локальная фильтрация делается экраном.
 *
 * Источник данных — ScoreboardEntry[] (событие scoreboard). Сортировка детерминирована:
 * по убыванию очков, затем по нику. FLIP-эффект работает в реальном браузере; в jsdom
 * (тесты) getBoundingClientRect отдаёт нули — дельта 0, transform не применяется, что
 * делает компонент безопасным для рендер-тестов (проверяется сортировка/подсветка).
 */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RosterEntry, ScoreboardEntry } from '../protocol';
import { DroppedBadge, useDroppedTooltip } from './DroppedBadge';
import { KickConfirmModal } from './KickConfirmModal';

export interface LiveLeaderboardProps {
  entries: ScoreboardEntry[];
  highlightNick?: string | null;
  title?: string;
  /** Показать только топ-N строк (0 = все). Удобно для экрана игрока. */
  limit?: number;
  /** Минимальная дельта (px), при которой применяется FLIP-анимация; меньшие скачки игнорируются. */
  animate?: boolean;
  /**
   * Фаза 15.1: roster-слоты комнаты (событие roster/roster_delta) для статусов
   * «отвалился» и сопоставления playerId кнопки кика. Не задан/пуст — рейтинг
   * работает как прежде (игрок и старые вызовы).
   */
  roster?: RosterEntry[];
  /** Ведущий: callback удаления участника (host-команда kick). Если не задан — контролы скрыты. */
  onKickPlayer?: (playerId: number) => void;
}

const MEDALS = ['🥇', '🥈', '🥉'];

/** Цель открытого confirm-модала удаления (null — модал закрыт). */
interface KickTarget {
  playerId: number;
  nick: string;
}

/** Строка рейтинга после слияния scoreboard↔roster (fallback — без места/очков). */
interface BoardRow {
  nick: string;
  /** null — fallback-строка из roster (первый вопрос ещё не закрыт). */
  score: number | null;
  /** null — fallback-строка: места нет. */
  rank: number | null;
  /** Сопоставленный roster-слот (по нику); undefined — в roster записи нет. */
  slot?: RosterEntry;
}

export function LiveLeaderboard({
  entries,
  highlightNick = null,
  title,
  limit = 0,
  animate = true,
  roster,
  onKickPlayer,
}: LiveLeaderboardProps): JSX.Element {
  const { t } = useTranslation();
  const hostControls = onKickPlayer !== undefined;
  const [kickTarget, setKickTarget] = useState<KickTarget | null>(null);

  // roster↔scoreboard по нику (см. док-комментарий модуля).
  const rosterByNick = useMemo(() => {
    const m = new Map<string, RosterEntry>();
    roster?.forEach((e) => m.set(e.nick, e));
    return m;
  }, [roster]);

  // Fallback (Фаза 15.1): scoreboard пуст, но roster непуст → строки из roster
  // (ник + статус + кик; без места/очков) — кик/dropped доступны в первом
  // активном вопросе. После первого закрытия приходит scoreboard — обычный режим.
  const fallback = entries.length === 0 && (roster?.length ?? 0) > 0;

  const rows: BoardRow[] = useMemo(() => {
    if (fallback) {
      const base = roster ?? [];
      const list = limit > 0 ? base.slice(0, limit) : base;
      return list.map((slot) => ({ nick: slot.nick, score: null, rank: null, slot }));
    }
    const sorted = [...entries].sort((a, b) => b.score - a.score || a.nick.localeCompare(b.nick));
    const shown = limit > 0 ? sorted.slice(0, limit) : sorted;
    return shown.map((e, i) => ({
      nick: e.nick,
      score: e.score,
      rank: i + 1,
      slot: rosterByNick.get(e.nick),
    }));
  }, [entries, fallback, limit, roster, rosterByNick]);

  const listRef = useRef<HTMLOListElement>(null);
  const prevPositions = useRef<Map<string, number>>(new Map());

  // FLIP: после коммита DOM (до покраски) инвертируем смещение переставленных строк,
  // затем в следующем кадре возвращаем transform в 0 с CSS-transition — строка
  // «переезжает». see CSS .live-row { transition: transform .3s ease; }.
  //
  // Сброс transform в '' идемпотентен, поэтому rAF намеренно НЕ отменяется в cleanup:
  // при быстрых ре-рендерах отмена могла бы пропустить «play» и оставить строки
  // смещёнными. Сброс стиля оторванных узлов при размонтировании безопасен (no-op).
  useLayoutEffect(() => {
    if (!animate) return;
    const list = listRef.current;
    if (!list) return;

    const els = new Map<string, HTMLElement>();
    list.querySelectorAll<HTMLElement>('[data-lb-key]').forEach((el) => {
      const key = el.dataset.lbKey;
      if (key) els.set(key, el);
    });

    // FIRST: натуральные новые позиции (до transform).
    const cur = new Map<string, number>();
    els.forEach((el, key) => cur.set(key, el.getBoundingClientRect().top));

    // LAST/INVERT: для строк, которые были и раньше — компенсируем смещение.
    els.forEach((el, key) => {
      const prev = prevPositions.current.get(key);
      if (prev === undefined) return; // новая строка — enter-анимация не обязательна.
      const delta = prev - (cur.get(key) ?? 0);
      if (Math.abs(delta) >= 1) {
        el.style.transition = 'none';
        el.style.transform = `translateY(${delta}px)`;
      }
    });

    // PLAY: убираем инверт в следующем кадре — браузер анимирует transform -> 0.
    scheduleFrame(() => {
      els.forEach((el) => {
        el.style.transition = '';
        el.style.transform = '';
      });
    });

    // Запоминаем натуральные позиции для следующего прохода.
    prevPositions.current = cur;
  });

  return (
    <section className="scoreboard live-leaderboard" aria-label={t('leaderboard.aria')}>
      {title ? <h3 className="block-title">{title}</h3> : null}
      {rows.length === 0 ? (
        <p className="muted">{t('leaderboard.empty')}</p>
      ) : (
        <ol className="live-list">
          {rows.map((row) => (
            <LiveRow
              key={row.nick}
              row={row}
              isMe={row.nick === highlightNick}
              hostControls={hostControls}
              onKickRequest={(playerId, nick) => setKickTarget({ playerId, nick })}
            />
          ))}
        </ol>
      )}
      {kickTarget !== null ? (
        <KickConfirmModal
          nick={kickTarget.nick}
          onCancel={() => setKickTarget(null)}
          onConfirm={() => {
            onKickPlayer?.(kickTarget.playerId);
            setKickTarget(null);
          }}
        />
      ) : null}
    </section>
  );
}

/** Одна строка рейтинга: место/медали, ник, бейдж dropped, очки, кнопка кика. */
function LiveRow({
  row,
  isMe,
  hostControls,
  onKickRequest,
}: {
  row: BoardRow;
  isMe: boolean;
  /** Контролы ведущего (Фаза 15.1): бейдж dropped + кнопка кика. */
  hostControls: boolean;
  onKickRequest: (playerId: number, nick: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const medal = row.rank !== null && row.rank <= MEDALS.length ? MEDALS[row.rank - 1] : null;
  const dropped = hostControls && row.slot?.status === 'dropped';
  const droppedTitle = useDroppedTooltip(
    row.slot ?? { playerId: 0, nick: row.nick, nickLocked: false },
  );
  const title = dropped ? droppedTitle : undefined;

  return (
    <li
      data-lb-key={row.nick}
      className={`live-row${isMe ? ' live-row--me' : ''}${dropped ? ' live-row--dropped' : ''}`}
      title={title}
    >
      {row.rank !== null ? (
        <span
          className="live-row__rank"
          aria-label={t('leaderboard.placeAria', { n: row.rank })}
        >
          {medal ?? row.rank}
        </span>
      ) : null}
      <span className="live-row__nick">{row.nick}</span>
      {dropped ? <DroppedBadge /> : null}
      {row.score !== null ? <span className="live-row__score">{row.score}</span> : null}
      {hostControls && row.slot ? (
        <button
          type="button"
          className="btn btn--sm btn--ghost roster-kick"
          data-testid={`roster-kick-${row.slot.playerId}`}
          onClick={() => onKickRequest(row.slot!.playerId, row.nick)}
        >
          {t('roster.kick.button')}
        </button>
      ) : null}
    </li>
  );
}

/** requestAnimationFrame с fallback на setTimeout (для окружений без rAF, напр. jsdom). */
function scheduleFrame(cb: () => void): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => cb());
  } else {
    setTimeout(() => cb(), 0);
  }
}
